import { useEffect, useMemo, useRef, useState } from 'react';

import ProjectDate from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/project-date';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { selectProjectEndTimestamp, selectProjectStartTimestamp } from '@/store/slices/project/selectors';
import useClickAwayListener from '@/utils/use-click-away-listener';
import useOnFocusAny from '@/utils/use-on-focus-any';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import CalendarDay from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-runtime/calendar-day';
import DateDescription from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/project-runtime/date-description';
import { getLocalDateInfo } from '@/utils/dates';
import toastTrigger from '@ui/toast/toast-trigger';
import { DateRange } from '@ui/calendar/types';
import { getUserTimezone } from '@ui/calendar/utils';
import EditTextWrapper from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/edit-text-wrapper';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import useHasPermission from '@/utils/authentication/use-has-permission';

import styles from './edit-project-dates.module.css';
import { EToastTypes } from '@ui/toast/toast.types';

const EditProjectDates = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dateContainerRef = useRef<HTMLDivElement>(null);

  const startTimestamp = useAppSelector(selectProjectStartTimestamp);
  const endTimestamp = useAppSelector(selectProjectEndTimestamp);
  const { hasPermission } = useHasPermission({ allowedRoles: GENERAL_EDITABLE_PERMISSIONS });

  const hasDates = !!(startTimestamp && endTimestamp);

  const [selectedDates, setSelectedDates] = useState<DateRange>({
    startDate: startTimestamp ? startTimestamp : null,
    endDate: endTimestamp ? endTimestamp : null,
    selectedTimezone: getUserTimezone(),
  });

  const dispatch = useAppDispatch();

  const handleUpdate = ({ startDate, endDate }: DateRange) => {
    if (!startDate || !endDate) return;

    if (startDate > endDate) {
      toastTrigger('Start date cannot be after end date', {
        type: EToastTypes.error,
        closeOnClick: true,
      });
      return;
    }
    // Update the project dates in the store or database
    dispatch(
      saveWorkingProjectByFields({
        start_timestamp: startDate,
        end_timestamp: endDate,
      })
    );
  };

  const [isEditing, setIsEditing] = useState(false);

  useClickAwayListener({
    ref: containerRef,
    onPointerDownInside: () => {
      setIsEditing(true);
    },
    onPointerUpOutside: () => {
      setIsEditing(false);
    },
    onTabAway: () => {
      setIsEditing(false);
    },
    open: true,
  });

  const isFocused = useOnFocusAny({ ref: containerRef });

  useEffect(() => {
    setIsEditing(isFocused);
  }, [isFocused]);

  const dates = useMemo(() => {
    return getLocalDateInfo({ startTimestamp, endTimestamp });
  }, [startTimestamp, endTimestamp]);

  return (
    <Container
      ref={containerRef}
      className={`${styles.container} ${isEditing || !hasDates ? styles.editing : ''}`}
      height="108px"
      data-component="EditProjectDates"
    >
      <Container className={`cursor-pointer ${styles.infoContainer}`} height="100%" width="100%">
        <Container className={`flex fd-row jc-space-between ai-center padding-l-4 margin-b-8 width-100-percent`}>
          <Text className="L3 padding-l-4 flex ai-center" color="var(--gray-300)">
            BASIC INFO
          </Text>
          <div className={styles.editLabel}>
            <EditTextWrapper />
          </div>
        </Container>
        <Container
          height="84px"
          borderRadius="12px"
          padding="8px"
          className={`flex-column jc-center ${styles.infoContent}`}
        >
          <Container className={`flex ai-center jc-flex-start gap-12`} height="100%" width="100%">
            <div>
              <CalendarDay dates={dates} />
            </div>
            <div>
              <DateDescription dates={dates} />
            </div>
          </Container>
        </Container>
      </Container>

      <Container className={styles.editContainer} ref={dateContainerRef}>
        <ProjectDate
          isInEditMode={isEditing}
          timestamp={startTimestamp}
          end_timestamp={endTimestamp}
          disabled={!hasPermission}
          onChange={setSelectedDates}
          onClose={() => {
            if (selectedDates?.startDate && selectedDates.endDate) {
              handleUpdate({
                startDate: selectedDates.startDate,
                endDate: selectedDates.endDate,
                selectedTimezone: selectedDates.selectedTimezone,
              });
            }
          }}
        />
      </Container>
    </Container>
  );
};

export default EditProjectDates;
